import React, { useState } from 'react';
import { Button, Chip, Icon } from '@saviynt/design-system';

import { LICENSE_INTELLIGENCE_FINDINGS_DATA } from '../../../../mock-data/licenseIntelligenceFindingsData';

import OPTIMIZATION_FINDINGS_CONSTANTS from './constants';
import { OptimizationFindingsPanel } from './OptimizationFindingsPanel';
import {
  type OptimizationFinding,
  type OptimizationFindingsProps,
} from './type';

import './OptimizationFindings.css';

/**
 * @description Fetches and displays top optimization findings with cost impact, metrics,
 * and a detail panel. Each finding card shows category, impact/effort chips, and savings
 * amount. Clicking a card opens the OptimizationFindingsPanel.
 * @param {OptimizationFindingsProps} props - Component props
 * @param {string} [props.vendor] - Vendor scope for the findings API (defaults to 'sap')
 * @returns {React.ReactElement} The optimization findings section with cards and side panel
 */
// Props (vendor, skip) accepted for future API integration
function OptimizationFindings(
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  _props: OptimizationFindingsProps
): React.ReactElement {
  const [selectedFinding, setSelectedFinding] =
    useState<OptimizationFinding | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Using local mock data instead of API call
  const { data } = LICENSE_INTELLIGENCE_FINDINGS_DATA;

  const isLoading = false;
  const isError = false;

  const refetch = () => {};

  const findings = data.findings ?? [];
  const netAnnualImpact = data.netAnnualImpact ?? 0;
  const { netAnnualImpactDisplayValue } = data;

  const handleFindingClick = (finding: OptimizationFinding): void => {
    setSelectedFinding(finding);
    setIsPanelOpen(true);
  };

  const handlePanelClose = (): void => {
    setIsPanelOpen(false);
    setSelectedFinding(null);
  };

  const handleApply = (): void => {
    // Handle deprovision action
  };

  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const getMetricChipColor = (
    value: string
  ): 'success' | 'warning' | 'critical' => {
    switch (value) {
      case 'High':
        return 'critical';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'success';
      default:
        return 'warning';
    }
  };

  const getCategoryChipColor = (
    category: string
  ): 'success' | 'info' | 'warning' | 'critical' => {
    switch (category) {
      case 'Cost Reduction':
        return 'success';
      case 'License Change':
        return 'info';
      case 'Role Optimization':
        return 'warning';
      default:
        return 'info';
    }
  };

  /** Show "Deprovision All" only for cost reduction findings with no recommended tier */
  const shouldShowDeprovision = (finding: OptimizationFinding): boolean =>
    finding.category === 'Cost Reduction' && finding.recommendedTier === null;

  if (isLoading) {
    return (
      <div className='LicenseManagement-OptimizationFindings'>
        <div className='LicenseManagement-OptimizationFindings-loading'>
          <Icon kind='SaviCompanion' size='smallMedium' />
          <p>{OPTIMIZATION_FINDINGS_CONSTANTS.LOADING}</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='LicenseManagement-OptimizationFindings'>
        <div className='LicenseManagement-OptimizationFindings-error'>
          <p>{OPTIMIZATION_FINDINGS_CONSTANTS.ERROR_MESSAGE}</p>
          <Button kind='outlined' onClick={refetch}>
            {OPTIMIZATION_FINDINGS_CONSTANTS.RETRY}
          </Button>
        </div>
      </div>
    );
  }

  const renderFindingCard = (
    finding: OptimizationFinding
  ): React.ReactElement => (
    <div
      key={finding.id}
      className='LicenseManagement-OptimizationFindings-findingCard'
      onClick={() => handleFindingClick(finding)}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleFindingClick(finding);
        }
      }}>
      {/* Finding Content */}
      <div className='LicenseManagement-OptimizationFindings-findingContent'>
        {/* Header with Title and Category Chip */}
        <div className='LicenseManagement-OptimizationFindings-findingHeader'>
          <p className='LicenseManagement-OptimizationFindings-findingTitle'>
            {finding.title}
          </p>
          <Chip
            kind='subtleFilled'
            label={finding.category}
            color={getCategoryChipColor(finding.category)}
            size='small'
          />
        </div>

        {/* Reason Text */}
        <p className='LicenseManagement-OptimizationFindings-findingDescription'>
          {finding.reasonText}
        </p>

        {/* Metrics Section */}
        <div className='LicenseManagement-OptimizationFindings-metricsSection'>
          {/* Impact Metric */}
          <div className='LicenseManagement-OptimizationFindings-metric'>
            <p className='LicenseManagement-OptimizationFindings-metricLabel'>
              {OPTIMIZATION_FINDINGS_CONSTANTS.IMPACT}
            </p>
            <Chip
              kind='outlined'
              label={finding.impact}
              color={getMetricChipColor(finding.impact)}
              size='small'
            />
          </div>

          {/* Effort Metric */}
          <div className='LicenseManagement-OptimizationFindings-metric'>
            <p className='LicenseManagement-OptimizationFindings-metricLabel'>
              {OPTIMIZATION_FINDINGS_CONSTANTS.EFFORT}
            </p>
            <Chip
              kind='outlined'
              label={finding.effort}
              color={getMetricChipColor(finding.effort)}
              size='small'
            />
          </div>
        </div>
      </div>

      {/* Cost Section */}
      <div className='LicenseManagement-OptimizationFindings-findingCostSection'>
        <p className='LicenseManagement-OptimizationFindings-savingsAmount'>
          {finding.savingsDisplayValue}
        </p>
        <p className='LicenseManagement-OptimizationFindings-savingsLabel'>
          {OPTIMIZATION_FINDINGS_CONSTANTS.LICENSE_SAVINGS}
        </p>
      </div>
    </div>
  );

  return (
    <div className='LicenseManagement-OptimizationFindings'>
      {/* Title Section */}
      <div className='LicenseManagement-OptimizationFindings-titleSection'>
        <div className='LicenseManagement-OptimizationFindings-titleLeft'>
          <Icon
            kind='SaviCompanion'
            size='smallMedium'
            className='LicenseManagement-OptimizationFindings-icon'
          />
          <h2 className='LicenseManagement-OptimizationFindings-titleText'>
            {OPTIMIZATION_FINDINGS_CONSTANTS.TOP_OPTIMIZATION_FINDINGS}
          </h2>
        </div>
        <div className='LicenseManagement-OptimizationFindings-costSection'>
          <p className='LicenseManagement-OptimizationFindings-costLabel'>
            {OPTIMIZATION_FINDINGS_CONSTANTS.NET_ANNUAL_IMPACT}
          </p>
          <p className='LicenseManagement-OptimizationFindings-costValue'>
            {netAnnualImpactDisplayValue ?? formatCurrency(netAnnualImpact)}
          </p>
        </div>
      </div>

      {/* List Items */}
      <div className='LicenseManagement-OptimizationFindings-listItems'>
        {findings.map((finding) => renderFindingCard(finding))}
      </div>

      {/* Optimization Findings Panel */}
      <OptimizationFindingsPanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        finding={selectedFinding}
        onApply={handleApply}
        showDeprovision={
          selectedFinding ? shouldShowDeprovision(selectedFinding) : false
        }
      />
    </div>
  );
}

export default OptimizationFindings;
